// src/pages/Attendance/OTSalary.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Alert,
    Spinner,
    Input,
    Label,
    FormGroup,
    Table,
} from 'reactstrap';
import {
    RefreshCw,
    Calendar,
    AlertCircle,
    Clock,
    Download,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import { format } from 'date-fns';

// Types
interface Designation {
    id: number;
    title: string;
}

interface Employee {
    id: number;
    name: string;
    employee_code: string;
    designation?: Designation;
    unit?: { id: number; name: string };
    status: number;
    basic_salary: number | null;
    overtime: boolean | null;
}

// OT Calculations - Updated to match backend
interface OTCalculations {
    total_days: number;
    per_day_salary: number;
    ot_per_hour_rate: number;
    total_ot_hours: number;
    ot_salary: number;
    full_day_hours: number;
    total_working_days: number;
}

interface EmployeeAttendance {
    employee: Employee;
    calculations: OTCalculations;
}

interface CompanySettings {
    full_day_time: number | null;
}

interface OTResponse {
    success: boolean;
    data: {
        month: number;
        year: number;
        total_days: number;
        type: 'ot';
        company_settings: CompanySettings;
        employees: EmployeeAttendance[];
    };
}

// Generate month options
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: format(new Date(2000, i, 1), 'MMMM'),
}));

// Generate year options (current year ± 5 years)
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 11 }, (_, i) => ({
    value: currentYear - 5 + i,
    label: (currentYear - 5 + i).toString(),
}));

const formatCurrency = (amount: number): string => {
    return '₹' + amount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const OTSalary: React.FC = () => {
    // State
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [salaryData, setSalaryData] = useState<OTResponse['data'] | null>(null);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Filters
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [filters, setFilters] = useState({
        designation_id: undefined as number | undefined,
        status: 1,
    });

    // Fetch OT salary data
    const fetchSalaryData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params: any = {
                year: selectedYear,
                month: selectedMonth,
            };

            if (filters.designation_id) {
                params.designation_id = filters.designation_id;
            }
            if (filters.status !== undefined) {
                params.status = filters.status;
            }

            const response = await apiClient.get('/salary/ot', params);

            if (response.success) {
                console.log('OT Salary data fetched:', response.data);
                setSalaryData(response.data);
            } else {
                setError(response.message || 'Failed to fetch OT salary data');
                toast.error('Failed to fetch OT salary data');
            }
        } catch (error: any) {
            console.error('Error fetching OT salary data:', error);
            setError(error.message || 'Failed to fetch OT salary data');
            toast.error('Failed to fetch OT salary data');
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, filters]);

    // Fetch designations
    const fetchDesignations = useCallback(async () => {
        try {
            const response = await apiClient.get('/designations');
            if (response?.data) {
                setDesignations(response.data);
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchDesignations();
        fetchSalaryData();
    }, []);

    // Handle filter changes
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            designation_id: undefined,
            status: 1,
        });
    };

    // Export to Excel
    const handleExportExcel = async () => {
        if (!salaryData || salaryData.employees.length === 0) {
            toast.warning('No data to export');
            return;
        }

        try {
            setExporting(true);

            const params = new URLSearchParams({
                year: selectedYear.toString(),
                month: selectedMonth.toString()
            });

            if (filters.designation_id) {
                params.append('designation_id', filters.designation_id.toString());
            }
            if (filters.status !== undefined && filters.status !== null) {
                params.append('status', filters.status.toString());
            }

            const token = localStorage.getItem('accessToken');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const url = `${API_URL}/salary/export/ot?${params.toString()}`;

            console.log('📊 Exporting OT salary to Excel:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });

            if (!response.ok) {
                let errorMessage = 'Export failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    const errorText = await response.text();
                    if (errorText) {
                        try {
                            const errorJson = JSON.parse(errorText);
                            errorMessage = errorJson.message || errorMessage;
                        } catch {
                            errorMessage = `Server error: ${response.status}`;
                        }
                    }
                }
                throw new Error(errorMessage);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
                throw new Error('Received empty file from server');
            }

            const monthName = format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM_yyyy');
            const filename = `OT_Salary_${monthName}.xlsx`;

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                URL.revokeObjectURL(link.href);
            }, 100);

            toast.success('OT salary exported successfully!');
        } catch (error: any) {
            console.error('❌ Export error:', error);
            toast.error(error.message || 'Failed to export OT salary');
        } finally {
            setExporting(false);
        }
    };

    // Filter employees with overtime enabled
    const getOTEmployees = () => {
        if (!salaryData) return [];
        return salaryData.employees.filter(item => item.employee.overtime === true);
    };

    const otEmployees = getOTEmployees();

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb
                    title="OT Salary"
                    pageTitle="Salary Attendance"
                />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="card-title mb-0">
                                        <Clock size={18} className="me-2" />
                                        OT Salary Register
                                    </h5>
                                    {salaryData && (
                                        <small className="text-muted">
                                            {format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}
                                            {' '}
                                            <Badge color="info" className="ms-2">
                                                {otEmployees.length} Employees with OT
                                            </Badge>
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={handleExportExcel}
                                        disabled={exporting || loading || otEmployees.length === 0}
                                    >
                                        {exporting ? (
                                            <>
                                                <Spinner size="sm" className="me-1" />
                                                Exporting...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={14} className="me-1" />
                                                Export Excel
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchSalaryData()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Filters */}
                                <div className="mb-3 p-3 border rounded bg-light">
                                    <Row className="g-3 align-items-end">
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Month</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedMonth}
                                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                    bsSize="sm"
                                                >
                                                    {monthOptions.map((m) => (
                                                        <option key={m.value} value={m.value}>
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Year</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedYear}
                                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                    bsSize="sm"
                                                >
                                                    {yearOptions.map((y) => (
                                                        <option key={y.value} value={y.value}>
                                                            {y.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Designation</Label>
                                                <Input
                                                    type="select"
                                                    value={filters.designation_id || ''}
                                                    onChange={(e) => handleFilterChange('designation_id', e.target.value ? parseInt(e.target.value) : undefined)}
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Designations</option>
                                                    {designations.map((d) => (
                                                        <option key={d.id} value={d.id}>
                                                            {d.title}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <div className="d-flex gap-2">
                                                    <Button color="light" size="sm" onClick={clearFilters} className="w-50">
                                                        Clear
                                                    </Button>
                                                    <Button color="primary" size="sm" onClick={fetchSalaryData} className="w-50">
                                                        Apply
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Error */}
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {/* OT Salary Table - Updated to match backend */}
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading OT salary data...</span>
                                    </div>
                                ) : !salaryData || otEmployees.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Clock size={48} className="text-muted mb-3" />
                                        <h5>No OT records found</h5>
                                        <p className="text-muted">
                                            {format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')} has no overtime-applicable employees
                                        </p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table bordered hover striped size="sm" className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                                            <thead className="table-light text-center" style={{ fontSize: '0.8rem' }}>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Emp Code</th>
                                                    <th>Employee Name</th>
                                                    <th>Designation</th>
                                                    <th>Company</th>
                                                    <th>Basic Salary</th>
                                                    <th>Total Days</th>
                                                    <th>Per Day Salary</th>
                                                    <th>Full Day Hours</th>
                                                    <th>OT Rate/Hour</th>
                                                    <th>Total OT Hours</th>
                                                    <th>OT Salary</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {otEmployees.map((item, index) => {
                                                    const employee = item.employee;
                                                    const cal = item.calculations;
                                                    const basicSalary = employee.basic_salary || 0;

                                                    return (
                                                        <tr key={employee.id}>
                                                            <td className="text-center">{index + 1}</td>
                                                            <td className="text-center">{employee.employee_code}</td>
                                                            <td className="fw-semibold">{employee.name}</td>
                                                            <td>{employee.designation?.title || '-'}</td>
                                                            <td>{employee.unit?.name || '-'}</td>
                                                            <td className="text-end">{formatCurrency(basicSalary)}</td>
                                                            <td className="text-center">{cal.total_working_days}</td>
                                                            <td className="text-end">{formatCurrency(cal.per_day_salary || 0)}</td>
                                                            <td className="text-center">{cal.full_day_hours || 8}</td>
                                                            <td className="text-end">{formatCurrency(cal.ot_per_hour_rate || 0)}</td>
                                                            <td className="text-center fw-medium text-info">{cal.total_ot_hours ? cal.total_ot_hours.toFixed(2) : '0.00'}</td>
                                                            <td className="text-end fw-bold text-success">{formatCurrency(cal.ot_salary || 0)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default OTSalary;